/**
 * The environment-free half of the audio layer.
 *
 * `schedule.ts` has to be importable by Node `.mjs` tools under type-stripping, which
 * means everything it reaches must avoid two things Node's loader will not do: resolve an
 * extensionless specifier, and rewrite a TypeScript parameter property. `sources.ts`
 * cannot satisfy the first — it pulls in the Cache API, `fetch` and an AudioContext — so
 * the two pure things the scheduler needs from it live here instead.
 */

import type { IslandPart, Recording } from '../types';

/**
 * Seconds of tone rendered per buffer. Long silences are chunked so a 30-minute gap never
 * becomes a 30-minute buffer, and each chunk gets fresh noise so nothing repeats.
 */
export const CHUNK_SEC = 20;

interface IslandFiles {
  files?: Partial<Record<'opus' | 'webm' | 'm4a', { path: string; bytes: number }>>;
}

/**
 * The island's parts, in source order.
 *
 * Older manifests describe an island with a single `files` map and no `parts`; that is
 * read as one part spanning the whole island, so both shapes flow through one path.
 */
export function islandParts(rec: Recording, index: number): IslandPart[] {
  const isl = rec.speech[index];
  if (isl?.parts?.length) return isl.parts;
  const flat = isl as unknown as IslandFiles;
  return [{ srcStart: isl.srcStart, srcEnd: isl.srcEnd, files: flat?.files }];
}
